import {
  DocumentData,
  QueryDocumentSnapshot,
  Timestamp,
} from "firebase/firestore";
import { FirebaseAppointment, OfflineAppointment } from "../../firestore/types";
import { Appointment } from "@elixir/types";
import { timestampToIsoString } from "../../firestore/utils";
import { getAppointmentAttributes } from "../../controllers/appointments/utils/get-apt-attributes";
import { getTeleLinks } from "../../apis/tryst/get-all-queues";

function convertFirestoreTimestampToEpoch(time?: Timestamp | null) {
  return time?.toMillis() || null;
}

/**
 * Converts a Firebase Firestore appointment document to an OfflineAppointment format.
 * @param aid - The appointment ID (should match the document ID)
 * @param doc - The Firestore document snapshot containing appointment data
 * @returns OfflineAppointment object with numeric timestamps and sync status
 */
export const firebaseAppointmentToOfflineAppointment = (
  aid: string,
  doc: QueryDocumentSnapshot<DocumentData, DocumentData>
): OfflineAppointment => {
  const apt = doc.data() as FirebaseAppointment;
  const isSynced = !doc.metadata.hasPendingWrites;
  return {
    ...apt,
    aid: aid,
    package_inclusions: apt.package_inclusions || [],
    date: convertFirestoreTimestampToEpoch(apt.date)!,
    full_date: convertFirestoreTimestampToEpoch(apt.full_date)!,
    start_time: convertFirestoreTimestampToEpoch(apt.start_time)!,
    end_time: convertFirestoreTimestampToEpoch(apt.end_time)!,
    receipts: apt.receipts || [],
    visit_checkin: convertFirestoreTimestampToEpoch(apt.visit_checkin),
    visit_start: convertFirestoreTimestampToEpoch(apt.visit_start),
    visit_end: convertFirestoreTimestampToEpoch(apt.visit_end),
    created_at: convertFirestoreTimestampToEpoch(apt.created_at),
    updated_at: convertFirestoreTimestampToEpoch(apt.updated_at),
    follow_up: convertFirestoreTimestampToEpoch(apt.follow_up),
    expires_at: convertFirestoreTimestampToEpoch(apt.expires_at),
    last_visit_time: convertFirestoreTimestampToEpoch(apt.last_visit_time),
    callback_requested: convertFirestoreTimestampToEpoch(
      apt.callback_requested
    ),
    business_id: apt.bid,
    is_local: !isSynced,
  };
};

/**
 * Converts a Firebase Firestore appointment document to a full Appointment object.
 * @param aid - The appointment ID (should match document ID)
 * @param doc - The Firestore document snapshot
 * @param patient - Patient data (MUST be valid and complete)
 * @param formData - Form data array (MUST be valid and complete)
 * @returns Appointment object with ISO string timestamps and computed fields
 */
export const firebaseAppointmentToAppointment = (
  aid: string,
  doc: QueryDocumentSnapshot<DocumentData, DocumentData>,
  patient: Appointment["patient"],
  formData: Appointment["formData"]
): Appointment => {
  const apt = doc.data() as FirebaseAppointment;
  const {
    clinic_id,
    receipts = [],
    status,
    call_status,
    callback_requested,
    channel,
    mode,
    payment_id,
    payment_status,
    prescription_id,
    prescription_url,
    visit_start,
  } = apt;
  
  return {
    ...apt,
    aid: aid,
    business_id: apt.bid,
    package_inclusions: apt.package_inclusions || [],
    date: timestampToIsoString(apt.date)!,
    full_date: timestampToIsoString(apt.full_date)!,
    start_time: timestampToIsoString(apt.start_time)!,
    end_time: timestampToIsoString(apt.end_time)!,
    visit_checkin: timestampToIsoString(apt.visit_checkin),
    visit_start: timestampToIsoString(apt.visit_start),
    visit_end: timestampToIsoString(apt.visit_end),
    created_at: timestampToIsoString(apt.created_at),
    updated_at: timestampToIsoString(apt.updated_at),
    follow_up: timestampToIsoString(apt.follow_up),
    last_visit_time: timestampToIsoString(apt.last_visit_time),
    callback_requested: timestampToIsoString(apt.callback_requested),
    ...getAppointmentAttributes({
      clinic_id,
      receipts,
      status,
      call_status,
      callback_requested: callback_requested?.toMillis(),
      channel,
      mode,
      payment_id,
      payment_status,
      prescription_id,
      prescription_url,
      visit_start: visit_start?.toMillis(),
    }),
    patient,
    formData,
    vendor_meta: [],
    vc_meta: getTeleLinks({ appointment: apt }),
  };
};

declare global {
  interface Window {
    firebaseAppointmentToOfflineAppointment: (
      aid: string,
      doc: QueryDocumentSnapshot<DocumentData, DocumentData>
    ) => OfflineAppointment;
    firebaseAppointmentToAppointment: (
      aid: string,
      doc: QueryDocumentSnapshot<DocumentData, DocumentData>,
      patient: Appointment["patient"],
      formData: Appointment["formData"]
    ) => Appointment;
  }
}

window.firebaseAppointmentToOfflineAppointment =
  firebaseAppointmentToOfflineAppointment;
window.firebaseAppointmentToAppointment = firebaseAppointmentToAppointment;
